<template>
  <div class="notification-container" data-notification>
    <transition-group name="notification" tag="div">
      <div 
        v-for="notification in notifications" 
        :key="notification.id"
        class="notification"
        :class="notification.type"
      >
        <div class="notification-content">
          <h4 class="notification-title">{{ notification.title }}</h4>
          <p class="notification-message">{{ notification.message }}</p>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { ref } from 'vue';

export default {
  name: 'AppNotification',
  setup() {
    const notifications = ref([]);
    
    // Function to add a notification
    const addNotification = (title, message, type = 'info') => {
      const id = Date.now();
      notifications.value.push({
        id,
        title,
        message,
        type
      });
      
      // Remove notification after 2.5 seconds
      setTimeout(() => {
        const index = notifications.value.findIndex(n => n.id === id);
        if (index !== -1) {
          notifications.value.splice(index, 1);
        }
      }, 2500);
    };
    
    // Welcome notification removed as requested
    
    // Make addNotification globally available
    window.showNotification = addNotification;
    
    // Also expose it through a more reliable method
    window.NotificationService = {
      show: addNotification
    };
    
    return {
      notifications,
      addNotification
    };
  }
};
</script>

<style scoped>
.notification-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  width: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none; /* Allow clicks to pass through when not directly interacting with notifications */
}

.notification {
  margin-bottom: 15px;
  padding: 18px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transform: translateY(0);
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: rgba(25, 25, 25, 0.95);
  border: 1px solid rgba(60, 60, 60, 0.5);
  backdrop-filter: blur(10px);
  width: 100%;
  text-align: center;
  pointer-events: auto; /* Enable clicks on the notification itself */
}

.notification.info {
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.2), rgba(33, 150, 243, 0.2));
  border-color: rgba(33, 150, 243, 0.4);
  box-shadow: 0 8px 25px rgba(33, 150, 243, 0.2);
}

.notification.success {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.25), rgba(139, 195, 74, 0.25));
  border-color: rgba(76, 175, 80, 0.5);
  box-shadow: 0 8px 25px rgba(76, 175, 80, 0.3);
}

.notification.warning {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.2), rgba(255, 152, 0, 0.2));
  border-color: rgba(255, 193, 7, 0.4);
  box-shadow: 0 8px 25px rgba(255, 193, 7, 0.2);
}

.notification.error {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.25), rgba(229, 57, 53, 0.25));
  border-color: rgba(244, 67, 54, 0.5);
  box-shadow: 0 8px 25px rgba(244, 67, 54, 0.3);
}

.notification-content {
  display: flex;
  flex-direction: column;
}

.notification-title {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: bold;
  color: #f0f0f0;
  text-shadow: 0 0 10px currentColor;
}

.notification-message {
  margin: 0;
  font-size: 0.95rem;
  color: #d0d0d0;
}

/* Animation */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.notification-enter-from {
  opacity: 0;
  transform: translateY(-50px) scale(0.8);
}
.notification-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.9);
}

/* Responsive styles */
@media (max-width: 768px) {
  .notification-container {
    width: calc(100% - 30px);
    top: 15px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  .notification {
    padding: 15px;
  }
}
</style>